import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../ui/dialog";
import { Trash2, FileText, Upload, Download, Lock, ShieldCheck, History as HistoryIcon, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const DOC_CATEGORIES = [
    { v: "teaser", label: "Teaser" },
    { v: "cim", label: "CIM" },
    { v: "nda", label: "NDA" },
    { v: "financials", label: "Financials" },
    { v: "spa", label: "SPA" },
    { v: "other", label: "Other" },
];

const ACCESS_LEVELS = [
    { v: "public", label: "Public", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    { v: "nda_required", label: "NDA required", color: "bg-amber-50 text-amber-700 border-amber-200" },
    { v: "qualified_only", label: "Qualified only", color: "bg-orange-50 text-orange-700 border-orange-300" },
    { v: "restricted", label: "Restricted", color: "bg-red-50 text-red-700 border-red-200" },
];
const ACCESS_BADGE = Object.fromEntries(ACCESS_LEVELS.map((a) => [a.v, a.color]));

// `dealId` is the original, still-supported prop (used by DealDetail.jsx).
// Phase 4 of the Distressed build generalises this component to a second
// parent type via `parentType`/`parentId` — e.g.
// <DocumentsTab parentType="distressed_mandate" parentId={mandateId} />.
// Buyer/NDA features (contacts fetch, NDA confirm inline, buyer column)
// only make sense for a deal's buyer pipeline, so they're skipped for any
// other parent type rather than shown against data that doesn't exist.
export default function DocumentsTab({ dealId, parentType, parentId, showAccessLog = true }) {
    const effectiveParentType = parentType || (dealId ? "deal" : null);
    const effectiveParentId = parentId ?? dealId;
    const isDeal = effectiveParentType === "deal";

    const [docs, setDocs] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [category, setCategory] = useState("teaser");
    const [accessLevel, setAccessLevel] = useState("");
    const [uploading, setUploading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [logOpen, setLogOpen] = useState(false);

    const refresh = async () => {
        const calls = [api.get(`/documents/${effectiveParentType}/${effectiveParentId}`)];
        if (isDeal) calls.push(api.get(`/contacts?type=buyer`));
        const [d, c] = await Promise.all(calls);
        setDocs(d.data);
        if (c) setContacts(c.data);
        setLoading(false);
    };
    useEffect(() => { refresh(); }, [effectiveParentType, effectiveParentId]); // eslint-disable-line react-hooks/exhaustive-deps

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 25 * 1024 * 1024) {
            toast.error("File too large (max 25MB)");
            return;
        }
        const fd = new FormData();
        fd.append("file", file);
        fd.append("category", category);
        if (accessLevel) fd.append("access_level", accessLevel);
        setUploading(true);
        try {
            await api.post(`/documents/${effectiveParentType}/${effectiveParentId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(`${file.name} uploaded`);
            e.target.value = "";
            refresh();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const download = async (doc) => {
        try {
            const response = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
            const url = URL.createObjectURL(response.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = doc.filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (err) {
            toast.error("Download failed");
        }
    };

    const del = async (id) => {
        if (!confirm("Delete this document?")) return;
        await api.delete(`/documents/${id}`);
        toast.success("Document removed");
        refresh();
    };

    const fmtSize = (bytes) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    return (
        <div className="space-y-4 max-w-5xl">
            <div className="border border-border rounded-md bg-white p-6">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className="font-display font-bold tracking-tight">Data room</h3>
                        <p className="text-xs text-muted-foreground mt-0.5">
                            {isDeal
                                ? "Upload Teasers, CIMs, NDAs and other deal files (max 25MB each)."
                                : "Upload accounts, correspondence and other mandate files (max 25MB each)."}
                        </p>
                    </div>
                    {showAccessLog && (
                        <Dialog open={logOpen} onOpenChange={setLogOpen}>
                            <DialogTrigger asChild>
                                <Button variant="outline" size="sm" className="gap-1.5" data-testid="open-access-log-btn">
                                    <HistoryIcon className="w-3.5 h-3.5" /> Access log
                                </Button>
                            </DialogTrigger>
                            <AccessLogDialog parentType={effectiveParentType} parentId={effectiveParentId} />
                        </Dialog>
                    )}
                </div>
                <div className="flex items-center gap-3 p-3 border border-dashed border-border rounded-md bg-muted/30">
                    <Select value={category} onValueChange={setCategory}>
                        <SelectTrigger className="w-36" data-testid="upload-category-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {DOC_CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={accessLevel || "_default"} onValueChange={(v) => setAccessLevel(v === "_default" ? "" : v)}>
                        <SelectTrigger className="w-44" data-testid="upload-access-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="_default">Default for category</SelectItem>
                            {ACCESS_LEVELS.map((a) => <SelectItem key={a.v} value={a.v}>{a.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <label className="flex-1">
                        <input type="file" className="hidden" onChange={onFile} disabled={uploading} data-testid="file-upload-input" />
                        <div className={`flex items-center justify-center gap-2 py-2 px-3 border border-border rounded-md text-sm cursor-pointer hover:bg-navy hover:text-white transition-colors ${uploading ? "opacity-50" : ""}`}>
                            <Upload className="w-4 h-4" />
                            {uploading ? "Uploading…" : "Choose file"}
                        </div>
                    </label>
                </div>
            </div>

            <div className="border border-border rounded-md bg-white overflow-hidden">
                {loading ? (
                    <div className="p-6 text-sm text-muted-foreground">Loading…</div>
                ) : docs.length === 0 ? (
                    <div className="p-12 text-center">
                        <FileText className="w-6 h-6 mx-auto text-muted-foreground mb-2 opacity-50" />
                        <p className="text-sm text-muted-foreground">No documents uploaded yet.</p>
                    </div>
                ) : (
                    <table className="w-full text-sm" data-testid="documents-table">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border bg-muted/40">
                                <th className="text-left px-5 py-3">File</th>
                                <th className="text-left px-5 py-3">Uploaded by</th>
                                <th className="text-right px-5 py-3">Size</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {docs.map((d) => (
                                <tr key={d.id} className="border-b border-border/60 hover:bg-muted/30" data-testid={`doc-row-${d.id}`}>
                                    <td className="px-5 py-3">
                                        <div className="flex items-center gap-2">
                                            <FileText className="w-4 h-4 text-navy flex-shrink-0" strokeWidth={1.5} />
                                            <span className="font-medium truncate">{d.filename}</span>
                                            {d.version > 1 && (
                                                <span className="text-[10px] uppercase tracking-wider font-mono text-gold border border-gold/40 bg-gold/10 px-1 py-0.5 rounded-sm">v{d.version}</span>
                                            )}
                                            {d.superseded_by && (
                                                <span className="text-[10px] uppercase tracking-wider text-muted-foreground italic">superseded</span>
                                            )}
                                            {d.nda_signed_by_buyer_id && (
                                                <span className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wider text-emerald-700">
                                                    <ShieldCheck className="w-3 h-3" /> signed
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-1.5 mt-1 ml-6">
                                            <Badge variant="outline" className="text-[10px] uppercase font-mono border-gold/40 text-gold">{d.category}</Badge>
                                            <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border font-medium ${ACCESS_BADGE[d.access_level || "public"] || ""}`}>
                                                <Lock className="w-2.5 h-2.5 inline mr-0.5" />{(d.access_level || "public").replace("_", " ")}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-5 py-3 text-muted-foreground text-xs">
                                        {d.uploaded_by_name || "—"}
                                        <div className="font-mono text-[10px]">{new Date(d.uploaded_at).toLocaleDateString()}</div>
                                    </td>
                                    <td className="px-5 py-3 text-right font-mono text-xs text-muted-foreground">{fmtSize(d.size)}</td>
                                    <td className="px-5 py-3 text-right whitespace-nowrap">
                                        {isDeal && d.category === "nda" && !d.nda_signed_by_buyer_id && (
                                            <ConfirmNdaInline doc={d} contacts={contacts} onDone={refresh} />
                                        )}
                                        <Button size="icon" variant="ghost" onClick={() => download(d)} data-testid={`doc-download-${d.id}`}>
                                            <Download className="w-3.5 h-3.5" />
                                        </Button>
                                        <Button size="icon" variant="ghost" onClick={() => del(d.id)} data-testid={`doc-delete-${d.id}`}>
                                            <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}


function ConfirmNdaInline({ doc, contacts, onDone }) {
    const [open, setOpen] = useState(false);
    const [buyerId, setBuyerId] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async () => {
        if (!buyerId) return toast.error("Select a buyer");
        setLoading(true);
        try {
            await api.post(`/documents/${doc.id}/confirm-nda`, { buyer_id: buyerId });
            toast.success("NDA confirmed; buyer status updated to NDA signed");
            setOpen(false);
            onDone();
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message);
        } finally { setLoading(false); }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button size="sm" variant="outline" className="h-7 text-xs gap-1 mr-1" data-testid={`confirm-nda-btn-${doc.id}`}>
                    <ShieldCheck className="w-3 h-3 text-emerald-600" /> Confirm NDA
                </Button>
            </DialogTrigger>
            <DialogContent data-testid="confirm-nda-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4 text-emerald-600" /> Confirm NDA signed
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-3 text-sm">
                    <p className="text-xs text-muted-foreground">Mark <span className="font-mono font-medium text-foreground">{doc.filename}</span> as signed by a buyer. This will unlock NDA-required documents for them and update their tracking status.</p>
                    <Select value={buyerId} onValueChange={setBuyerId}>
                        <SelectTrigger data-testid="confirm-nda-buyer-select">
                            <SelectValue placeholder="Select buyer who signed…" />
                        </SelectTrigger>
                        <SelectContent>
                            {contacts.length === 0 ? (
                                <SelectItem value="none" disabled>No buyers in your contacts</SelectItem>
                            ) : contacts.map((c) => (
                                <SelectItem key={c.id} value={c.id}>{c.name}{c.company ? ` · ${c.company}` : ""}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <DialogFooter>
                    <Button onClick={submit} disabled={loading || !buyerId} className="bg-foreground text-background hover:bg-neutral-800 hover:text-white" data-testid="confirm-nda-submit-btn">
                        {loading ? "Saving…" : "Confirm"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function AccessLogDialog({ parentType, parentId }) {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        api.get(`/documents/${parentType}/${parentId}/access-log`)
            .then((r) => setEvents(r.data))
            .catch(() => setEvents([]))
            .finally(() => setLoading(false));
    }, [parentType, parentId]);

    return (
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="access-log-dialog">
            <DialogHeader>
                <DialogTitle className="font-display flex items-center gap-2">
                    <HistoryIcon className="w-4 h-4 text-gold" /> Document access log
                </DialogTitle>
            </DialogHeader>
            {loading ? (
                <div className="text-sm text-muted-foreground py-6 text-center">Loading…</div>
            ) : events.length === 0 ? (
                <div className="text-sm text-muted-foreground py-6 text-center">No access events yet.</div>
            ) : (
                <table className="w-full text-sm" data-testid="access-log-table">
                    <thead>
                        <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                            <th className="text-left py-2">When</th>
                            <th className="text-left py-2">Action</th>
                            <th className="text-left py-2">Document</th>
                            <th className="text-left py-2">Buyer</th>
                            <th className="text-left py-2">User</th>
                            <th className="text-left py-2">Result</th>
                        </tr>
                    </thead>
                    <tbody>
                        {events.map((e) => (
                            <tr key={e.id} className="border-b border-border/60 last:border-0" data-testid={`access-log-row-${e.id}`}>
                                <td className="py-2 text-xs font-mono text-muted-foreground whitespace-nowrap">{new Date(e.created_at).toLocaleString()}</td>
                                <td className="py-2 text-xs"><Badge variant="outline" className="text-[10px] uppercase">{e.action}</Badge></td>
                                <td className="py-2 text-xs">{e.document?.filename || e.doc_id?.slice(0, 8)}</td>
                                <td className="py-2 text-xs">{e.buyer?.name || "—"}</td>
                                <td className="py-2 text-xs text-muted-foreground">{e.user?.name || "—"}</td>
                                <td className="py-2 text-xs">
                                    {e.allowed ? (
                                        <span className="text-emerald-700 inline-flex items-center gap-1"><ChevronRight className="w-3 h-3" /> {e.reason || "ok"}</span>
                                    ) : (
                                        <span className="text-red-700 inline-flex items-center gap-1"><Lock className="w-3 h-3" /> {e.reason || "denied"}</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </DialogContent>
    );
}

